import { useAuth0 } from '@auth0/auth0-react';
import { ReactNode, useEffect } from 'react';
import { trackException, trackEvent } from '../../utils/monitoring';
import { useNavigate } from 'react-router-dom';
import { TokenService } from '../../services/secureTokenService';
import './ErrorBoundary.less';
import { CirraServiceType } from '../../enums/auth';
import { getStoredReferrerData } from '../../utils/utmTracking';

interface AuthErrorBoundaryProps {
  service: CirraServiceType;
  children: ReactNode;
}

export const AuthErrorBoundary = ({
  service,
  children,
}: AuthErrorBoundaryProps) => {
  const { error } = useAuth0();
  const navigate = useNavigate();
  const title = 'Authentication Error';

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    // Note: Email verification callbacks (success=true&code=success) are handled
    // by useEmailVerificationHandler hook in App.tsx/AgentApp.tsx, which also
    // handles the account mismatch case. This component only handles Auth0 errors.

    const errorParam = params.get('error');
    const errorDescription = params.get('error_description');
    let newPath;
    let reasonForRedirect;
    if (errorParam === 'access_denied') {
      if (errorDescription?.toLowerCase().includes('verify your email')) {
        TokenService.clearTokens();
        newPath = '/verify-email';
        reasonForRedirect = 'verify email';
      } else if (
        errorDescription?.toLowerCase().includes('personal email') ||
        errorDescription?.toLowerCase().includes('work email')
      ) {
        newPath = '/signup-failed';
        reasonForRedirect = 'signup failed';
      }

      if (newPath) {
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname
        );

        // Get email from localStorage (stored during signup form submission)
        // Do NOT fall back to user?.email — when Auth0 returns access_denied,
        // the user object is from the cached Auth0 session (cacheLocation="localstorage")
        // and may belong to a completely different user.
        const emailToShow = localStorage.getItem('signup_email');

        navigate(newPath, { state: { service, email: emailToShow } });

        // Track navigation with UTM context if available (without PII)
        const signupData = getStoredReferrerData();
        trackEvent('Navigate', {
          to: newPath,
          reason: reasonForRedirect,
          email_domain: emailToShow?.split('@')[1] || undefined,
          ...(signupData || {}),
        });
        return;
      }
    }

    // For other errors, track and display them
    if (error) {
      error.name = title;
      trackException(error);
    }
  }, [error, navigate, service]);

  if (error) {
    return (
      <div className="error-boundary-container">
        <h2>{title}</h2>
        <pre>{error.message}</pre>
      </div>
    );
  }

  return <>{children}</>;
};
